import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UploadCloud, UserPlus, FileText, Zap } from "lucide-react";
import { WorkspaceShell } from "@/shell/WorkspaceShell";
import { railSeverityOfAlerts } from "@/screens/shared/briefing";
import { isOpenProjectStatus } from "@/domain/status";
import {
  Stat,
  SpendBudgetStat,
  ProgressBar,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  DataTable,
  type Column,
  Pill,
  EntityStatusPill,
  NarrativeCard,
  RightRail,
  RailSection,
  ActivityFeed,
  eventToActivityItem,
  Briefing,
  AttentionBanner,
  AttentionAction,
  Button,
} from "@/design/components";
import { ArrowRight } from "lucide-react";
import { ProjectHealthTable } from "./ProjectHealthTable";
import { deriveBriefingAlerts } from "@/screens/shared/briefing";
import { toBriefingAlerts } from "@/domain/actionPanel";
import { useStore } from "@/domain/store";
import {
  useSeed,
  useProgramme,
  useVisibleProjects,
  useIsContractor,
  useIsSubContractor,
  useVisibleSubProjects,
  useActiveProfile,
  useActiveWorkspace,
  useSubProjects,
  useOrgIndex,
  useTerminology,
  useMoney,
  useMoneyParts,
  useActionPanelItems,
} from "@/domain/hooks";
import type { SubProject } from "@/domain/types";

export function ProgrammeDashboard() {
  // Resolved first (see WorkspacePages.tsx's WorkspaceProjectsPage for the
  // full rationale) — every workspace-scoped hook below is threaded with it
  // explicitly rather than falling back to the global activeWorkspaceId
  // store, which a client-side nav into this page can still have pointed at
  // the previous workspace on this first render.
  const { workspaceId } = useParams();
  const seed = useSeed();
  const programme = useProgramme();
  const projects = useVisibleProjects(workspaceId);
  const isContractor = useIsContractor();
  // A sub-contractor PM (e.g. Naome) is scoped to their own sub-projects only.
  const isSubContractor = useIsSubContractor();
  const mySubs = useVisibleSubProjects(workspaceId);
  const profile = useActiveProfile();
  const orgs = useOrgIndex(workspaceId);
  const term = useTerminology();
  const money = useMoney();
  const mp = useMoneyParts();
  const ws = useActiveWorkspace();
  const navigate = useNavigate();
  const base = `/workspace/${workspaceId}`;
  const [railCollapsed, setRailCollapsed] = useState(false);

  const projectIds = useMemo(() => new Set(projects.map((p) => p.id)), [projects]);
  const mySubIds = useMemo(() => new Set(mySubs.map((s) => s.id)), [mySubs]);
  // Sub-contractors see their own sub-projects; everyone else sees the flagship project's subs.
  const allFlagshipSubs = useSubProjects(projects[0]?.id ?? "", workspaceId);
  const flagshipSubs = isSubContractor ? mySubs : allFlagshipSubs;

  const totals = useMemo(() => {
    // Governance: a sub-contractor's figures roll up from their sub-projects, never the
    // whole project. Everyone else rolls up from the visible projects.
    const scope = isSubContractor
      ? { budgeted: mySubs.reduce((s, x) => s + x.budgeted, 0), spent: mySubs.reduce((s, x) => s + x.spent, 0), evidenced: mySubs.reduce((s, x) => s + x.evidenced, 0), redundancy: 0 }
      : { budgeted: projects.reduce((s, p) => s + p.budgeted, 0), spent: projects.reduce((s, p) => s + p.spent, 0), evidenced: projects.reduce((s, p) => s + p.evidenced, 0), redundancy: projects.reduce((s, p) => s + p.redundancy, 0) };
    const pending = seed.spendRequests.filter((r) => {
      if (r.state !== "proposed" && r.state !== "flagged") return false;
      if (isSubContractor) return r.subProjectId ? mySubIds.has(r.subProjectId) : false;
      return !isContractor || projectIds.has(r.projectId);
    }).length;
    return { ...scope, pending };
  }, [projects, mySubs, seed.spendRequests, isContractor, isSubContractor, projectIds, mySubIds]);

  // The headline budget figure: the whole programme fund for oversight/funder, only the
  // contractor's own scoped budget for a contractor (governance).
  const headlineBudget = isContractor ? totals.budgeted : programme.totalBudget;

  const recentApprovals = seed.spendRequests
    .filter((r) => {
      if (r.state !== "approved_for_spend" && r.state !== "confirmed") return false;
      if (isSubContractor) return r.subProjectId ? mySubIds.has(r.subProjectId) : false;
      return !isContractor || projectIds.has(r.projectId);
    })
    .slice(0, 6);

  // Role-aware quick actions (no rare procurement upload here).
  const quickActions = isContractor
    ? []
    : profile.group === "funder"
    ? [
        { label: "Generate report", icon: FileText, onClick: () => navigate("/funder/reports") },
        { label: "Export data", icon: UploadCloud, onClick: () => navigate(`${base}/settings/audit`) },
      ]
    : [
        { label: "Onboard contractor", icon: UserPlus, onClick: () => navigate(`${base}/onboard-contractor`) },
      ];

  // Itemised analyst briefing for the rail, scoped to what this viewer governs.
  // Contractors get the same dynamic, priority-ranked action-panel feed their own
  // dashboard home reads (assigned work, requests, deadlines) rather than the
  // workspace-scoped pending-count/evidence-gap summary everyone else still sees here.
  const { items: contractorActionItems } = useActionPanelItems();
  const setWorkspace = useStore((s) => s.setWorkspace);
  const workspaceBriefingAlerts = deriveBriefingAlerts(
    seed,
    isSubContractor ? { subProjectIds: mySubs.map((s) => s.id) } : { projectIds: projects.map((p) => p.id) },
    money,
    (kind, label) => ({ label, onClick: () => navigate(kind === "evidence" ? `${base}/evidence` : `${base}/approvals`) }),
  );
  // Same reasoning as StratusDashboardLayout/FunderDashboardLayout: an item
  // can concern a different workspace than the one currently open — pin the
  // right one before its action link navigates.
  const briefingAlerts = isContractor
    ? toBriefingAlerts(contractorActionItems).map((a) => ({
        ...a,
        action: a.action && {
          ...a.action,
          onClick: a.workspaceId ? () => setWorkspace(a.workspaceId!) : a.action.onClick,
        },
      }))
    : workspaceBriefingAlerts;
  const lastEventAt = seed.auditEvents[0]?.timestamp;

  const subColumns: Column<SubProject>[] = [
    { key: "name", header: term.subProject, cell: (sp) => <span className="font-medium text-ink">{sp.name}</span> },
    { key: "contractor", header: "Contractor", cell: (sp) => <span className="text-mute">{orgs[sp.leadContractorId]?.name}</span> },
    { key: "budget", header: "Budgeted", align: "right", cell: (sp) => <span className="tabular-nums text-mute">{money(sp.budgeted)}</span> },
    {
      key: "spent",
      header: "Spent",
      cell: (sp) => (
        <div className="w-28">
          <span className="text-sm tabular-nums text-ink">{money(sp.spent)}</span>
          <ProgressBar value={sp.budgeted ? sp.spent / sp.budgeted : 0} className="mt-1" />
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (sp) => <EntityStatusPill status={sp.status} />,
    },
  ];

  return (
    <WorkspaceShell
      rightRail={
        <RightRail
          collapsed={railCollapsed}
          onToggle={() => setRailCollapsed((c) => !c)}
          summary={[{ id: "briefing", count: briefingAlerts.length, severity: railSeverityOfAlerts(briefingAlerts) }]}
        >
          {quickActions.length > 0 && (
            <RailSection title="Quick actions" tone="action" icon={Zap}>
              <div className="flex flex-col gap-2">
                {quickActions.map((a) => (
                  <Button key={a.label} variant="secondary" className="justify-start" onClick={a.onClick}>
                    <a.icon className="h-4 w-4" /> {a.label}
                  </Button>
                ))}
              </div>
            </RailSection>
          )}
          <RailSection title="Briefing" divider>
            <Briefing alerts={briefingAlerts} updatedAt={lastEventAt} />
          </RailSection>
          <RailSection title="Activity">
            <ActivityFeed density="compact" items={seed.auditEvents.slice(0, 6).map((e) => eventToActivityItem(e, { meta: orgs[e.actorOrganisationId]?.name, amount: e.amount ? money(e.amount) : undefined }))} />
          </RailSection>
        </RightRail>
      }
    >
      <div className="px-4 py-5 md:px-8 md:py-6">
        {isContractor ? (
          <>
            <h1 className="text-2xl font-bold tracking-tight text-ink">Your workspace</h1>
            <p className="mt-1 text-sm text-mute">
              {isSubContractor
                ? `${profile.organisation} · ${mySubs.length} ${term.subProject.toLowerCase()}${mySubs.length === 1 ? "" : "s"} on ${projects[0]?.name ?? programme.name}`
                : `${profile.organisation} · ${projects.length} project${projects.length === 1 ? "" : "s"} in ${programme.name}`}
            </p>
          </>
        ) : (
          /* Signature hero: the one bold place. Cover, lockup, status, headline figure. */
          <section className="relative overflow-hidden rounded-xl border border-line">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `linear-gradient(90deg, rgba(13,16,27,0.94) 0%, rgba(13,16,27,0.70) 52%, rgba(13,16,27,0.40) 100%)${ws.coverImageUrl ? `, url(${ws.coverImageUrl})` : ""}`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              aria-hidden
            />
            <span className="absolute left-0 top-0 h-full w-1" style={{ background: "var(--accent-base)" }} aria-hidden />
            {totals.pending > 0 && (
              <button
                onClick={() => navigate(`${base}/approvals`)}
                className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-warn/30 bg-warn/20 px-3 py-1 text-xs font-medium text-warn backdrop-blur-sm hover:bg-warn/30"
              >
                <span className="tabular-nums">{totals.pending}</span>
                {totals.pending === 1 ? " spend ask" : " spend asks"} pending
                <ArrowRight className="h-3 w-3" />
              </button>
            )}
            <div className="relative flex flex-col gap-5 p-6 md:flex-row md:items-end md:justify-between md:p-7">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-white/45">Programme overview</p>
                <h1 className="mt-2 text-[1.85rem] font-bold tracking-tight text-white leading-tight">{programme.name}</h1>
                <p className="mt-1.5 text-sm text-white/55">Funded by {ws.funder.name} · Oversight by {ws.oversight.name}</p>
              </div>
              <div className="flex shrink-0 items-end gap-5">
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-widest text-white/40">Total committed</p>
                  <p className="mt-1 whitespace-nowrap font-mono text-3xl font-bold leading-none tabular-nums text-white">
                    {mp(headlineBudget).value}
                    <span className="ml-1.5 text-base font-semibold text-white/50">{mp(headlineBudget).unit}</span>
                  </p>
                </div>
                <HeroStatus state={programme.alertState} />
              </div>
            </div>
          </section>
        )}

        {/* Attention banner — the contractor's primary call to action, above the figures. */}
        {isContractor && totals.pending > 0 && (
          <AttentionBanner
            className="mt-5"
            title={`${totals.pending} spend ask${totals.pending > 1 ? "s" : ""} awaiting your approval`}
            action={
              <AttentionAction onClick={() => navigate(`${base}/approvals`)}>
                Review {totals.pending} spend ask{totals.pending > 1 ? "s" : ""} <ArrowRight className="h-3.5 w-3.5" />
              </AttentionAction>
            }
          />
        )}

        {/* Stat hero strip. Budget and spend are one combined card: the headline figure is the
            committed fund, with a brand-tone "spent of budget" line beneath — burn is never
            RAG-coloured here (health lives in its own pill / table). */}
        <section className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat label="Pending approvals" value={`${totals.pending}`} unit="spend asks" sub={totals.pending ? "Awaiting review" : "All clear"} subTone={totals.pending ? "warn" : "ok"} hero onClick={totals.pending ? () => navigate(`${base}/approvals`) : undefined} />
          <div className="flex flex-col rounded-card border border-line bg-card p-4">
            <span className="text-meta text-faint">{isSubContractor ? "Your scope budget" : isContractor ? "Your budget" : "Total budgeted"}</span>
            <span className="mt-2.5 flex items-baseline gap-1.5">
              <span className="num whitespace-nowrap text-bigfig font-semibold text-ink">{mp(headlineBudget).value}</span>
              <span className="text-sm font-medium text-faint">{mp(headlineBudget).unit}</span>
            </span>
            <span className="mt-2 text-xs text-faint">{isSubContractor ? `across ${mySubs.length} ${term.subProject.toLowerCase()}${mySubs.length === 1 ? "" : "s"}` : isContractor ? `across ${projects.length} project${projects.length === 1 ? "" : "s"}` : "across 35 fire stations"}</span>
            <SpendBudgetStat
              className="mt-3"
              label="Spent of budget"
              spent={totals.spent}
              budget={headlineBudget}
              spentLabel={money(totals.spent)}
              budgetLabel={money(headlineBudget)}
            />
          </div>
          <Stat label="Evidenced and confirmed" value={mp(totals.evidenced).value} unit={mp(totals.evidenced).unit} hero trend={<ProgressBar value={totals.evidenced / Math.max(1, totals.spent)} tone="ok" />} />
          {isSubContractor ? (
            <Stat label={`${term.subProject}s`} value={`${mySubs.filter((s) => isOpenProjectStatus(s.status)).length}`} unit="" sub={`of ${mySubs.length} active`} hero />
          ) : isContractor ? (
            <Stat label="Active projects" value={`${projects.filter((p) => isOpenProjectStatus(p.status)).length}`} unit="" sub={`of ${projects.length}`} hero />
          ) : (
            <Stat label="Contingency held" value={mp(totals.redundancy).value} unit={mp(totals.redundancy).unit} sub="25% of budget" hero />
          )}
        </section>

        {/* Project health table — hidden for sub-contractors (governance: no whole-project view) */}
        {!isSubContractor && (
          <section className="mt-8 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-faint">{isContractor ? "Your project health" : "Project health"}</h2>
              <button onClick={() => navigate(`${base}/projects`)} className="text-sm font-medium text-brand hover:underline">View all</button>
            </div>
            <ProjectHealthTable projects={projects} onOpen={(p) => navigate(`${base}/projects/${p.id}`)} />
          </section>
        )}

        {/* Content cards */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>{isSubContractor ? `Your ${term.subProject.toLowerCase()}s` : `${term.subProject}s`}</CardTitle>
                <Pill variant="outline">{flagshipSubs.length}</Pill>
              </CardHeader>
              <CardBody className="pt-4">
                <DataTable
                  columns={subColumns}
                  rows={flagshipSubs}
                  rowKey={(sp) => sp.id}
                  onRowClick={(sp) => navigate(`${base}/projects/${projects[0]?.id}/sub/${sp.id}`)}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recent approvals</CardTitle>
              </CardHeader>
              <CardBody className="pt-2">
                <ul className="divide-y divide-hairline">
                  {recentApprovals.map((r) => (
                    <li key={r.id} className="flex items-center justify-between py-2.5">
                      <span className="text-sm text-ink">{r.title}</span>
                      <span className="text-sm tabular-nums text-mute">{money(r.amount)}</span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          </div>

          <div className="space-y-6">
            <NarrativeCard
              variant="briefing"
              title="This week"
              body={seed.briefing.whereWeAre}
              figures={[
                { label: "Budget consumed", value: "47%" },
                { label: "Time elapsed", value: "42%" },
              ]}
            />
            <NarrativeCard
              variant="flag"
              title="Burn rate vs time"
              body="61% budget consumed vs 42% time elapsed. Pace is 5.3% ahead of plan. Needs attention."
              figures={[{ label: "Pace vs plan", value: "+5.3%", tone: "warn" }]}
            />
            <Card>
              <CardBody>
                <p className="text-sm font-semibold text-ink">Next milestone: Roofing</p>
                <p className="mt-1 text-sm text-mute">14 days to target. Evidence requirements: roof manifest, completion photo per fire station, contractor sign-off.</p>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </WorkspaceShell>
  );
}

/** Dark-surface status chip for the programme hero. */
function HeroStatus({ state }: { state: "GREEN" | "AMBER" | "RED" }) {
  const map = {
    GREEN: { dot: "bg-ok", label: "On track" },
    AMBER: { dot: "bg-warn", label: "Needs attention" },
    RED: { dot: "bg-bad", label: "At risk" },
  }[state];
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
      <span className={`h-1.5 w-1.5 rounded-full ${map.dot}`} aria-hidden />
      {map.label}
    </span>
  );
}
